import { supabase } from './supabaseClient';

// Retry helper for Supabase auth lock contention (navigator lock "steal" AbortError).
// The underlying operation is idempotent here — retry transparently.
const isLockError = (err) =>
  err?.message?.includes("Lock broken") ||
  err?.details?.includes("Lock broken") ||
  err?.name === 'AbortError';

export async function withAuthRetry(fn, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i < retries && isLockError(err)) {
        await new Promise((r) => setTimeout(r, 150 * (i + 1)));
        continue;
      }
      throw err;
    }
  }
}

// ==================== AUTH ====================

export const auth = {
  async signUp(email, password, nome) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { nome },
      },
    });
    if (error) throw error;
    return data;
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/painel',
      },
    });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  async getProfile() {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .single();
    if (error) throw error;
    return data;
  },

  async updateFontSize(tamanho_fonte) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('profiles')
      .update({ tamanho_fonte })
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateAvatar(avatar) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('profiles')
      .update({ avatar })
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteAccount() {
    await withAuthRetry(async () => {
      const { error } = await supabase.rpc('delete_user_account');
      if (error) throw error;
    });
    // Clear any Supabase-managed storage keys directly. Calling signOut would
    // POST to /auth/v1/logout and 403 because the user no longer exists.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      // Private mode / storage blocked — ignore; full reload will flush session.
    }
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// ==================== MESES ====================

export const meses = {
  async list() {
    const { data, error } = await supabase
      .from('meses')
      .select('*')
      .order('ano', { ascending: false })
      .order('mes', { ascending: false });
    if (error) throw error;
    return data;
  },

  async getOrCreate(mes, ano) {
    const { data, error } = await supabase.rpc('get_or_create_mes', {
      p_mes: mes,
      p_ano: ano,
    });
    if (error) throw error;
    return data;
  },

  async copiarAnterior(mes, ano) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.rpc('copiar_mes_anterior', {
      p_user_id: user.id,
      p_mes: mes,
      p_ano: ano,
    });
    if (error) throw error;
    return data;
  },

  async copiarReceitasEsporadicasAnterior(mes, ano) {
    const { data, error } = await supabase.rpc('copiar_receitas_esporadicas_anterior', {
      p_mes: mes,
      p_ano: ano,
    });
    if (error) throw error;
    return data;
  },

  async copiarDespesasEsporadicasAnterior(mes, ano) {
    const { data, error } = await supabase.rpc('copiar_despesas_esporadicas_anterior', {
      p_mes: mes,
      p_ano: ano,
    });
    if (error) throw error;
    return data;
  },
};

// ==================== RECEITAS ====================

export const receitas = {
  async list(mesId) {
    const { data, error } = await supabase
      .from('receitas')
      .select('*')
      .eq('mes_id', mesId)
      .order('created_at');
    if (error) throw error;
    return data;
  },

  async create(receitaData, { startMes = null, startAno = null } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('receitas')
      .insert({ ...receitaData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    if (data.is_fixo) {
      await supabase.rpc('propagar_receita_fixa', {
        p_receita_id: data.id,
        p_start_mes: startMes,
        p_start_ano: startAno,
      });
    }
    return data;
  },

  async update(id, receitaData) {
    const { data, error } = await supabase
      .from('receitas')
      .update(receitaData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    if (data.is_fixo) {
      await supabase.rpc('propagar_receita_fixa', { p_receita_id: data.id });
    }
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('receitas')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// ==================== DESCONTOS ====================

export const descontos = {
  async list(mesId) {
    const { data, error } = await supabase
      .from('descontos')
      .select('*')
      .eq('mes_id', mesId)
      .order('created_at');
    if (error) throw error;
    return data;
  },

  async create(descontoData, { startMes = null, startAno = null } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('descontos')
      .insert({ ...descontoData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    if (data.is_fixo) {
      await supabase.rpc('propagar_desconto_fixo', {
        p_desconto_id: data.id,
        p_start_mes: startMes,
        p_start_ano: startAno,
      });
    }
    return data;
  },

  async update(id, descontoData) {
    const { data, error } = await supabase
      .from('descontos')
      .update(descontoData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    if (data.is_fixo) {
      await supabase.rpc('propagar_desconto_fixo', { p_desconto_id: data.id });
    }
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('descontos')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// ==================== DESPESAS RECORRENTES ====================

export const despesasRecorrentes = {
  async list(mesId) {
    const { data, error } = await supabase
      .from('despesas_recorrentes')
      .select('*')
      .eq('mes_id', mesId)
      .order('data_vencimento');
    if (error) throw error;
    return data;
  },

  async create(despesaData, { startMes = null, startAno = null } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('despesas_recorrentes')
      .insert({ ...despesaData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    if (data.is_fixo) {
      await supabase.rpc('propagar_despesa_recorrente_fixa', {
        p_despesa_id: data.id,
        p_start_mes: startMes,
        p_start_ano: startAno,
      });
    }
    return data;
  },

  async update(id, despesaData, { propagate = true } = {}) {
    const { data, error } = await supabase
      .from('despesas_recorrentes')
      .update(despesaData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    if (data.is_fixo && propagate) {
      await supabase.rpc('propagar_despesa_recorrente_fixa', { p_despesa_id: data.id });
    }
    return data;
  },

  async togglePago(id) {
    const { data: current, error: readError } = await supabase
      .from('despesas_recorrentes')
      .select('pago')
      .eq('id', id)
      .single();
    if (readError) throw readError;

    const { data, error } = await supabase
      .from('despesas_recorrentes')
      .update({ pago: !current.pago })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('despesas_recorrentes')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// ==================== DESPESAS ESPORADICAS ====================

export const despesasEsporadicas = {
  async list(mesId) {
    const { data, error } = await supabase
      .from('despesas_esporadicas')
      .select('*')
      .eq('mes_id', mesId)
      .order('created_at');
    if (error) throw error;
    return data;
  },

  async create(despesaData) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('despesas_esporadicas')
      .insert({ ...despesaData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id, despesaData) {
    const { data, error } = await supabase
      .from('despesas_esporadicas')
      .update(despesaData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async togglePago(id) {
    const { data: current, error: readError } = await supabase
      .from('despesas_esporadicas')
      .select('pago')
      .eq('id', id)
      .single();
    if (readError) throw readError;

    const { data, error } = await supabase
      .from('despesas_esporadicas')
      .update({ pago: !current.pago })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('despesas_esporadicas')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// ==================== METAS ====================

export const metasPoupanca = {
  async list() {
    const { data, error } = await supabase
      .from('metas_poupanca')
      .select('*')
      .order('created_at');
    if (error) throw error;
    return data;
  },

  async create(metaData) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('metas_poupanca')
      .insert({ ...metaData, user_id: user.id })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id, metaData) {
    const { data, error } = await supabase
      .from('metas_poupanca')
      .update(metaData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('metas_poupanca')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// ==================== DEPOSITOS ====================

export const depositos = {
  async list(metaId) {
    const { data, error } = await supabase
      .from('depositos_meta')
      .select('*')
      .eq('meta_id', metaId)
      .order('data_deposito', { ascending: false });
    if (error) throw error;
    return data;
  },

  async create(depositoData) {
    const { data, error } = await supabase.rpc('add_deposito_meta', {
      p_meta_id: depositoData.meta_id,
      p_mes_id: depositoData.mes_id || null,
      p_valor: depositoData.valor,
      p_data_deposito: depositoData.data_deposito,
      p_observacao: depositoData.observacao || null,
    });
    if (error) throw error;
    return data;
  },

  async delete(depositoId) {
    const { data, error } = await supabase.rpc('delete_deposito_meta', {
      p_deposito_id: depositoId,
    });
    if (error) throw error;
    return data;
  },
};

// ==================== USER CATEGORIES ====================

export const userCategories = {
  async list(tipo) {
    const { data, error } = await supabase
      .from('user_categories')
      .select('*')
      .eq('tipo', tipo)
      .order('created_at');
    if (error) throw error;
    return data;
  },

  async create(nome, tipo, icon = null) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('user_categories')
      .insert({ user_id: user.id, nome, tipo, icon })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase
      .from('user_categories')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};

// ==================== RESUMO / RELATORIO ====================

export const relatorios = {
  async getResumoMensal(mesId) {
    const { data, error } = await supabase.rpc('get_resumo_mensal', {
      p_mes_id: mesId,
    });
    if (error) throw error;
    return data;
  },

  async getRelatorioAnual(ano) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.rpc('get_relatorio_anual', {
      p_user_id: user.id,
      p_ano: ano,
    });
    if (error) throw error;
    return data;
  },
};

// ==================== CONTATO ====================

export const contato = {
  async enviar({ assunto, mensagem }) {
    const { data, error } = await supabase.functions.invoke('send-contact-email', {
      body: { assunto, mensagem },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },
};

// ==================== RECEIPT PARSING ====================

export const recibos = {
  async parse(base64Image, mediaType = 'image/jpeg') {
    const { data, error } = await supabase.functions.invoke('parse-receipt', {
      body: { image: base64Image, mediaType },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },
};
